import { ArrowPathIcon } from "@heroicons/react/20/solid";
import { TrashIcon } from "@heroicons/react/24/solid";
import { useApplicationModals } from "app/common/hooks";
import { useBarcodeScanner } from "app/context/BarcodeScannerContext";
import { resolveProductBarcode } from "app/common/resolveProductBarcode";
import {
  BadgeButton,
  Button,
  DottedOutlineButton,
  IconButton,
} from "app/components/atoms/button";
import { FormHeader, FormSummary } from "app/components/atoms/layout";
import Table from "app/components/atoms/table";
import Typography from "app/components/atoms/typography";
import { InputMolecule } from "app/components/molecules/form";
import ImageWithPreview from "app/components/molecules/imageWithPreview";
import { ModalMolecule } from "app/components/molecules/modal";
import {
  SelectMolecule,
  SelectSearchMolecule,
  ProductSelectSearchMolecule,
} from "app/components/molecules/select";
import api from "app/integration/api";
import {
  useCollector,
  useCreateForm,
  usePaginatedCollector,
} from "app/integration/common/hooks";
import MainLayout from "app/layouts/main";
import AppModals from "app/modals";
import clsx from "clsx";
import { FieldArray, Formik, getIn } from "formik";
import str from "lib/str";
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import style from "style/height.module.css";
import {
  filter as _filter,
  findWhere as _findWhere,
  flatten as _flatten,
  where as _where,
  filter,
} from "underscore";
import * as Yup from "yup";
import { isEmpty } from "underscore";
import { useToast } from "app/components/atoms/toast/toast";
import { MinusCircleIcon, PlusIcon } from "@heroicons/react/24/outline";

const breadcrumbItems = [
  { label: "Sales", href: "/sales_order" },
  { label: "Order", href: "/sales_order" },
  { label: "Tambah Order", href: "#" },
];

function CreateSalesOrderPage() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  let isError = false;

  const handleSuccess = (res) => {
    showToast("Product berhasil ditambahkan", "success");
    console.log(res);
    navigate("/sales_order/update/" + res.data.id);
  };

  const [isLoading, setIsloading] = useState(false);
  const [duplicateOrder, setDuplicateOrder] = useState(false);
  const [confirmedDuplicateOrder, setConfirmedDuplicateOrder] = useState(false);

  const [showInvoice, setShowInvoice] = useState(false);
  const [delivery_fee, setDeliveryFee] = useState(0);
  const [needUpdateDeliveryFee, setNeedUpdateDeliveryFee] = useState(true);

  const [salesOrders, setSalesOrders, salesOrdersLoader, collectSalesOrders] =
    useCollector({ modelName: "salesOrder" });
  const [salesChannels] =
    useCollector({ modelName: "salesChannel" });
  const formSalesOrder = useRef(null);

  const [productParams, setProductParams] = useState({
    search: "",
    product_search: "",
    order: "code",
    sort: "asc",
    status: null,
    customer_id: null,
    page: 1,
    limit: 50,
  });

  useEffect(() => {
    collectProducts(productParams);
  }, [productParams]);

  const [
    products,
    setProducts,
    productsLoader,
    collectProducts,
    productPaginationProps,
  ] = usePaginatedCollector({ modelName: "product", params: productParams });

  const [invoices, setInvoices, invoicesLoader, collectInvoices] = useCollector(
    { modelName: "invoice" }
  );

  const [productVariants, setProductVariants, productVariantsLoader, collectProductVariants] =
    useCollector({ modelName: "productVariant" });

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const duplicateProductCheck = (products) => {
    // check for duplicate object
    const seen = new Set();

    // Ensure products is an array
    const productArray = Array.isArray(products)
      ? products
      : products?.data || products?.items || [];

    for (const product of productArray) {
      if (isEmpty(product)) continue;
      const key = `${product.product_id}_${product.size_variant_id}_${product.color_variant_id}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
    }
    return true;
  };

  const { initialValues, validationSchema, onSubmit } = useCreateForm({
    initialValues: {
      customer_id: null,
      customer_name: null,
      address_id: null,
      customer_address: null,
      delivery_fee: 0,
      note_order: "",
      courier_package: "",
      sales_channel_id: null,
      discount_transaction: 0,
      is_endorsement: false,
      items: [],
    },
    validationSchema: Yup.object().shape({
      customer_id: Yup.number().required("Customer harus diisi"),
      // address_id: Yup.number().required("Alamat harus diisi"),
      delivery_fee: Yup.number(),
      sales_channel_id: Yup.number().required("Channel harus diisi"),
      note_order: Yup.string(),
      items: Yup.array()
        .of(
          Yup.object().shape({
            product_id: Yup.number().required("Produk harus diisi"),
            size_variant_id: Yup.number().required("Size harus diisi"),
            color_variant_id: Yup.number().required("Warna harus diisi"),
            qty: Yup.number().required("Qty harus diisi"),
            unit_price: Yup.number().required("Harga harus diisi"),
            discount_reseller: Yup.number()
              .min(0, "discount tidak boleh negatif")
              .test(
                'max-discount',
                'Diskon tidak boleh melebihi harga satuan',
                function (value) {
                  if (!value) return true; // Allow empty values
                  const { unit_price } = this.parent;
                  return value <= unit_price;
                }
              )
          })
        )
        .min(1, "Minimal harus ada 1 Item yang dibeli.")
        .test(
          "Unique",
          "Terdapat produk dengan varian yang sama. Jika varian sama persis, harap menambah melalui QTY.",
          (values) => {
            return duplicateProductCheck(values);
          }
        ),
    }),
    onSubmit: async (values) => {
      // if (needUpdateDeliveryFee) {
      //   setPayloads("errorMessage.view", {
      //     title: "Update Sales Order Error",
      //     message: "Harap kalkulasi ulang ongkir terlebih dahulu.",
      //   });
      //   openModal("errorMessage.view");
      //   isError = true;
      // } else {
      // }
      try {
        const filteredSalesOrders = _where(salesOrders, {
          customer_id: values.customer_id,
          delivery_to: values.deliveryTo,
          address_id: values.address_id,
          sales_channel_id: values.sales_channel_id,
        });

        // Ada order yang mirip gak?
        if (filteredSalesOrders.length > 0) {
          setDuplicateOrder(true);

          // User kalau belum konfirmasi bikin order yang sama, jangan bikin order nya
          if (!confirmedDuplicateOrder) {
            return;
          }
        }

        console.log("values: ", values);

        setIsloading(true);
        let response = await api.salesOrder.store(
          values.customer_id,
          values.address_id,
          values.sales_channel_id,
          values.note_order,
          delivery_fee,
          values.courier_package,
          values.is_endorsement,
          values.discount_transaction,
          values.items
            .filter((orderDetail) => !isEmpty(orderDetail))
            .map((orderDetail) => ({
              product_id: orderDetail.product_id,
              size_variant_id: orderDetail.size_variant_id,
              color_variant_id: orderDetail.color_variant_id,
              qty: orderDetail.qty,
              unit_price: orderDetail.unit_price,
              discount_reseller: orderDetail.discount_reseller || 0,
              weight: orderDetail.weight,
              notes: orderDetail.notes,
            }))
        );
        isError = false;
        return response;
      } catch (error) {
        if (error?.response?.data?.message) {
          setPayloads("errorMessage.view", {
            title: "Create Sales Order",
            message: error?.response?.data?.message,
          });
          isError = true;
          openModal("errorMessage.view");
          setIsloading(false);
        } else {
          setPayloads("errorMessage.view", {
            title: "Create Sales Order",
            message: "Server Error",
          });
          setIsloading(false);
          navigate("/sales_order");
        }
      }
    },
    onSuccess: handleSuccess,
  });

  const [expandedNotes, setExpandedNotes] = useState({});

  const [customerParams, setCustomerParams] = useState({
    search: "",
    page: 1,
    limit: Number(process.env.REACT_APP_CUSTOMER_LIMIT),
  });
  const [customers, setMCustomers, customersLoader, collectMCustomers] =
    useCollector({ modelName: "customer", params: customerParams });

  useEffect(() => {
    collectMCustomers(customerParams);
  }, [customerParams]);

  const [variants] = useCollector({ modelName: "variant" });

  const { openModal, setPayloads } = useApplicationModals();
  const [currentErrors, setCurrentErrors] = useState([]);

  const [selectedCourier, setSelectedCourier] = useState("");

  function runValidations(values) {
    validationSchema
      .validate(values, { abortEarly: false })
      .then((responseData) => {
        console.log("no validation errors");
        console.log(responseData);
        setCurrentErrors([]);
      })
      .catch((err) => {
        console.log(err);
        console.log(err.name); // ValidationErrors
        console.log(err.errors);
        setCurrentErrors(err.errors);

        setPayloads("errorMessage.view", {
          title: "Tambah Order Error",
          data: err.errors,
        });
        openModal("errorMessage.view");
      });
  }

  const localHelpers = {
    variantItemsByVariantId: (variantId) => {
      const variantCategories = _findWhere(variants, {
        id: variantId,
      })?.VariantCategories;
      const variantItems = _flatten(
        variantCategories?.map((variantCategory) => variantCategory.Variants)
      );
      return variantItems;
    },
    variantItemById: (variantItemId) => {
      const variantItems = _flatten(
        variants.map((variant) => {
          return _flatten(
            variant.VariantCategories.map(
              (variantCategory) => variantCategory.Variants
            )
          );
        })
      );
      return _findWhere(variantItems, { id: variantItemId });
    },
    subtotal: (orderItems) => {
      let subtotal = orderItems
        .map(
          (orderItem) => orderItem ? +orderItem?.qty *
            (+orderItem?.unit_price - orderItem?.discount_reseller) : 0
        )
        .reduce((partialSum, a) => partialSum + a, 0);

      return +subtotal;
    },
    totalWeight: (orderItems) => {
      let totalWeightOrder = orderItems
        .map((orderItem) => orderItem ? +orderItem?.qty * +orderItem?.weight : 0)
        .reduce((partialSum, a) => partialSum + a, 0);

      totalWeightOrder = Math.ceil(totalWeightOrder / 1000) * 1000;

      return +totalWeightOrder;
    },
  };

  function customerInvoices(customer_id) {
    return _where(invoices, { customer_id: customer_id });
  }

  function getUniqueData(product, product_id, variant, selected_variant_id) {
    let uniqueData = {};
    let isDisabled = false;

    product?.ProductVariants?.forEach((item) => {
      if (variant === "color" && selected_variant_id > 0) {
        if (
          product?.ProductVariants.find(
            (option) =>
              option.size.id === selected_variant_id &&
              option.color.id === item[`${variant}`].id
          ) === undefined
        ) {
          isDisabled = true;
        } else {
          isDisabled = false;
        }
      }
      if (variant === "size" && selected_variant_id > 0) {
        if (
          product?.ProductVariants?.find(
            (option) =>
              option.color.id === selected_variant_id &&
              option.size.id === item[`${variant}`].id
          ) === undefined
        ) {
          isDisabled = true;
        } else {
          isDisabled = false;
        }
      }
      uniqueData[item[`${variant}`].name] = {
        id: item[`${variant}`].id,
        name: item[`${variant}`].name,
        disabled: isDisabled,
      };
    });

    let convertData = Object.entries(uniqueData).map((item) => {
      return {
        value: item[1].id,
        label: item[1].name,
        disabled: item[1].disabled,
      };
    });

    return convertData;
  }

  /**
   * Menghasilkan opsi dropdown varian (size / color) dengan fallback label
   * jika varian yang terpilih pada order detail belum terindeks di ProductVariants.
   *
   * @param {Object} product - Objek produk yang dipilih (berisi ProductVariants).
   * @param {number|string} productId - ID produk saat ini.
   * @param {"size"|"color"} variantType - Tipe varian yang akan diambil ("size" atau "color").
   * @param {number|string} dependentVariantId - ID varian pasangan/pendamping (misal: color_variant_id saat mencari size, atau sebaliknya).
   * @param {Object} orderDetail - Objek item baris order saat ini (berisi fallback id dan label nama varian).
   * @returns {Array<{value: number|string, label: string, disabled?: boolean}>} Daftar opsi untuk SelectMolecule dropdown.
   * @sideEffects Tidak ada (pure function).
   */
  function getVariantOptionsWithFallback(
    product,
    productId,
    variantType,
    dependentVariantId,
    orderDetail
  ) {
    const rawOpts = getUniqueData(
      product,
      productId,
      variantType,
      dependentVariantId
    );
    const opts = Array.isArray(rawOpts) ? [...rawOpts] : [];

    if (variantType === "size") {
      if (
        orderDetail?.size_variant_id &&
        !opts.some((opt) => String(opt.value) === String(orderDetail.size_variant_id))
      ) {
        opts.unshift({
          value: orderDetail.size_variant_id,
          label: orderDetail.size_name || "All Size",
        });
      }
    } else if (variantType === "color") {
      if (
        orderDetail?.color_variant_id &&
        !opts.some((opt) => String(opt.value) === String(orderDetail.color_variant_id))
      ) {
        opts.unshift({
          value: orderDetail.color_variant_id,
          label: orderDetail.color_name || "All Color",
        });
      }
    }

    return opts;
  }

  async function getCustomerAddress(address_id) {
    const response = await api.address.show(address_id);
    return response?.data?.city_id;
  }


  const displayName = (customers) => {
    const customerList = [];
    customers?.map((customer) => {
      let label = "";
      if (customer?.firstName) label += customer?.firstName;
      if (customer?.middleName) label += ` ${customer?.middleName}`;
      if (customer?.lastName) label += ` ${customer?.lastName}`;
      if (customer?.nickName) label += ` (${customer?.nickName})`;
      if (customer?.phone) label += ` - ${customer?.phone}`;

      customerList.push({
        value: customer.id,
        label,
      });
    });
    return customerList;
  };

  async function getDeliveryFee(address_id, items, type) {
    if (!address_id) {
      setPayloads("errorMessage.view", {
        title: "Get Ongkir Error",
        message:
          "Tidak dapat kalkulasi ongkir jika alamat tidak ditemukan. Harap set alamat dengan benar.",
      });
      isError = true;
      openModal("errorMessage.view");
      return;
    }

    let destination = await getCustomerAddress(address_id);
    let totalWeightOrder = items
      .map((orderItem) => +orderItem.qty * +orderItem.weight)
      .reduce((partialSum, a) => partialSum + a, 0);

    // Round up by 1000
    totalWeightOrder = Math.ceil(totalWeightOrder / 1000) * 1000;

    if (totalWeightOrder <= 0) {
      setPayloads("errorMessage.view", {
        title: "Get Ongkir Error",
        message:
          "Tidak dapat kalkulasi ongkir jika berat produk 0. Harap set berat produk dengan benar.",
      });
      isError = true;
      openModal("errorMessage.view");
      return;
    }

    if (!destination) {
      setPayloads("errorMessage.view", {
        title: "Get Ongkir Error",
        message:
          "Tidak dapat kalkulasi ongkir jika alamat tidak ditemukan. Harap set alamat dengan benar.",
      });
      isError = true;
      openModal("errorMessage.view");
      return;
    }

    let deliveryFee = 0;

    if (destination && totalWeightOrder > 0) {
      const response = await api.rajaOngkir.domestic(
        destination,
        totalWeightOrder
      );

      if (response && response.data.length > 0) {
        // Check all data rows for matching service
        let foundService = false;
        for (const data of response.data) {
          const filtered = data?.costs.filter(i => i.service == type)[0];
          if (filtered) {
            setDeliveryFee((filtered?.cost[0]?.value || 0));
            setNeedUpdateDeliveryFee(false);
            foundService = true;
            deliveryFee = filtered?.cost[0]?.value || 0;
            break;
          }
        }

        if (!foundService) {
          setDeliveryFee(0);
          setNeedUpdateDeliveryFee(true);
          alert("Kurir tidak tersedia");
          deliveryFee = 0;
        }
      }
    }

    if (formSalesOrder.current) {
      const values = formSalesOrder.current?.values;
      formSalesOrder.current.setFieldValue("discount_transaction", Number(values.is_endorsement ? (Number(localHelpers.subtotal(values.items)) + Number(deliveryFee)) : 0));
    }
  }

  const [selectedProducts, setSelectedProducts] = useState([]);

  const handleProductSelected = (selectedProduct, key) => {
    // Save selectedProduct into an array of selectedProducts based on index key
    setSelectedProducts((prevSelectedProducts) => {
      const updatedSelectedProducts = [...prevSelectedProducts];
      updatedSelectedProducts[key] = selectedProduct;
      return updatedSelectedProducts;
    });
  };

  useEffect(() => {
    if (formSalesOrder.current) {
      const values = formSalesOrder.current?.values;
      console.log("values: ", values);
    }
  }, [selectedProducts]);


  /**
   * Menangani penambahan produk ke dalam form pesanan penjualan (Sales Order).
   * Memperbarui kuantitas produk jika varian sudah ada di keranjang secara immutable,
   * atau menambahkan baris item baru, serta memperbarui kalkulasi diskon endorsement.
   *
   * @param {Object} product - Objek data produk terformat { product, variant }.
   * @sideEffects Mengupdate Formik state values.items dan values.discount_transaction secara immutable, serta menampilkan toast sukses.
   */
  const handleSuccessProductChoose = (product) => {
    if (formSalesOrder.current) {
      const values = formSalesOrder.current?.values;

      const existingItemIndex = values.items?.findIndex(
        (existingItem) =>
          existingItem.product_id === product?.product?.id &&
          existingItem.size_variant_id === product?.variant?.size?.id &&
          existingItem.color_variant_id === product?.variant?.color?.id
      );

      if (existingItemIndex !== -1) {
        // Jika produk sudah ada di keranjang, tambah kuantitas secara immutable
        const updatedItems = [...values.items];
        updatedItems[existingItemIndex] = {
          ...updatedItems[existingItemIndex],
          qty: Number(updatedItems[existingItemIndex].qty || 0) + 1,
        };
        formSalesOrder.current.setFieldValue("items", updatedItems);
        formSalesOrder.current.setFieldValue(
          "discount_transaction",
          values.is_endorsement
            ? Number(localHelpers.subtotal(updatedItems)) + Number(delivery_fee)
            : 0
        );
        setSelectedProducts((prev) => {
          const updated = [...prev];
          if (!updated[existingItemIndex]) {
            updated[existingItemIndex] = product?.product;
          }
          return updated;
        });
        setExpandedNotes((prev) => ({ ...prev, [existingItemIndex]: true }));
      } else {
        // Jika produk belum ada, tambahkan item baru ke daftar
        const newIndex = values.items.length;
        const newItems = [
          ...values.items,
          {
            product_id: product?.product?.id,
            size_variant_id: product?.variant?.size?.id,
            color_variant_id: product?.variant?.color?.id,
            size_name: product?.variant?.size?.name || "All Size",
            color_name: product?.variant?.color?.name || "All Color",
            qty: 1,
            unit_price: product?.product?.price,
            discount_reseller: 0,
            weight: product?.product?.weight,
            imageUrl: product?.product?.imageUrl || product?.rawVariant?.imageUrl || "",
          },
        ];
        formSalesOrder.current.setFieldValue("items", newItems);
        setSelectedProducts((prev) => {
          const updated = [...prev];
          updated[newIndex] = product?.product;
          return updated;
        });
        setExpandedNotes((prev) => ({ ...prev, [newIndex]: true }));
        formSalesOrder.current.setFieldValue(
          "discount_transaction",
          values.is_endorsement
            ? Number(localHelpers.subtotal(newItems)) + Number(delivery_fee)
            : 0
        );
      }
      showToast("Anda telah menambahkan produk ke dalam keranjang", "success");
    }
  };

  /**
   * Menangani pembacaan barcode hardware global untuk halaman Tambah Sales Order.
   * Melakukan resolusi barcode bertingkat (memori lokal -> API varian -> API unit -> API SKU produk).
   * Menambahkan item ke pesanan jika ditemukan, atau menampilkan pesan toast error merah jika tidak ditemukan.
   *
   * @param {string} scannedBarcode - Nilai barcode yang telah dibersihkan oleh scanner buffer.
   * @returns {Promise<void>}
   * @sideEffects Mengupdate Formik items atau menampilkan pesan error toast jika barcode tidak ada.
   */
  const handleBarcodeScan = async (scannedBarcode) => {
    if (!scannedBarcode) return;
    try {
      const resolvedProduct = await resolveProductBarcode(scannedBarcode, productVariants);
      if (resolvedProduct) {
        handleSuccessProductChoose(resolvedProduct);
      } else {
        showToast(`Barcode "${scannedBarcode}" tidak ditemukan!`, "error");
      }
    } catch (err) {
      console.error("[SalesOrder Create] Error scanning barcode:", err);
      showToast(`Gagal memproses barcode "${scannedBarcode}"`, "error");
    }
  };

  useBarcodeScanner(handleBarcodeScan, { info: 'Sales Order' });

  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const handleSuccessCustomerChoose = (customer) => {
    if (formSalesOrder.current) {
      setSelectedCustomer(customer);
      formSalesOrder.current.setFieldValue("customer_id", customer?.id);
      formSalesOrder.current.setFieldValue("customer_name", customer?.nickName);
      formSalesOrder.current.setFieldValue("address_id", customer?.customer_address[0]?.id);
      formSalesOrder.current.setFieldValue("customer_address", `${customer?.customer_address[0]?.desc}, Prov. ${customer?.customer_address[0]?.state_name}, Kab/kota ${customer?.customer_address[0]?.city_name}, Kec. ${customer?.customer_address[0]?.district_name}, Kel. ${customer?.customer_address[0]?.sub_district_name}, Kodepos ${customer?.customer_address[0]?.zipcode}`);
      formSalesOrder.current.setFieldValue("deliveryTo", customer?.id);
      formSalesOrder.current.setFieldValue("note_order", customer?.customer_address[0]?.note_order);
      formSalesOrder.current.setFieldValue("deliveryTime", customer?.customer_address[0]?.delivery_time);
      formSalesOrder.current.setFieldValue("parkingFee", customer?.customer_address[0]?.parking_fee);
      setNeedUpdateDeliveryFee(true);
      showToast("Customer berhasil dipilih", "success");
    }
  };

  return (
    <MainLayout
      activeSidebarNavigation="sales"
      activeSidebarChild="order"
      breadcrumbItems={breadcrumbItems}
      pageTitle="Tambah Order"
      headingButtons={[
        {
          label: "Pilih Customer",
          type: "button",
          onClick: () => {
            setPayloads("customer.choose");
            openModal("customer.choose");
          },
        },
        {
          label: "Pilih Product",
          type: "button",
          onClick: () => {
            setPayloads("product.choose");
            openModal("product.choose");
          },
        },
        {
          type: "icon",
          icon: "camera",
          title: "Scan Product QR", // This will be shown as a tooltip
          onClick: () => {
            setPayloads("product.scanQrCode");
            openModal("product.scanQrCode");
          },
        },
      ]}
    >
      <AppModals
        items={["errorMessage.view", "customer.choose", "product.choose", "product.scanQrCode"]}
        onSuccess={{
          "errorMessage.view": () => fetch(),
          "customer.choose": handleSuccessCustomerChoose,
          "product.choose": handleSuccessProductChoose,
          "product.scanQrCode": handleSuccessProductChoose,
        }}
      />
      <Formik
        {...{ initialValues, validationSchema, onSubmit }}
        enableReinitialize={false}
        innerRef={formSalesOrder}
      >
        {({
          values,
          errors,
          status,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          setFieldValue,
        }) => (
          <form
            className={clsx(
              "px-2 overflow-y-auto",
              style["main-content-height"]
            )}
            onSubmit={handleSubmit}
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") {
                e.preventDefault();
              }
            }}
          >
            <FormHeader>
              <SelectSearchMolecule testId="sales-order-create-customer-id-select"
                label="Customer"
                name="customer_id"
                options={displayName(customers)}
                onChange={(e) => {
                  handleChange(e);
                  setFieldValue("deliveryTo", e.target.value);
                  const customer = _findWhere(customers, {
                    id: e.target.value,
                  });
                  setSelectedCustomer(customer);
                  setFieldValue(
                    "address_id",
                    customer?.customer_address[0]?.id
                  );
                  setFieldValue(
                    "note_order",
                    customer?.customer_address[0]?.note_order
                  );
                  setFieldValue(
                    "deliveryTime",
                    customer?.customer_address[0]?.delivery_time
                  );
                  setFieldValue(
                    "parkingFee",
                    customer?.customer_address[0]?.parking_fee
                  );
                  setNeedUpdateDeliveryFee(true);
                }}
                onSearch={(text) => {
                  setCustomerParams({ ...customerParams, search: text });
                }}
                apiSearch={true}
                onBlur={handleBlur}
                value={values.customer_id}
                errorMessage={
                  errors.customer_id &&
                  touched.customer_id &&
                  errors.customer_id
                }
              />
              <SelectMolecule testId="sales-order-create-address-id-select"
                label="Alamat Kirim"
                name="address_id"
                options={selectedCustomer?.customer_address?.map((address) => ({
                  value: address.id,
                  label: address.desc,
                }))}
                value={values?.address_id}
              />
              <SelectMolecule testId="sales-order-create-sales-channel-id-select"
                label="Channel"
                name="sales_channel_id"
                options={salesChannels?.map((channel) => ({
                  value: channel.id,
                  label: channel.name,
                }))}
                onChange={(e) => {
                  handleChange(e);
                  setFieldValue("sales_channel_id", e.target.value);
                }}
                onBlur={handleBlur}
                value={values.sales_channel_id}
                errorMessage={
                  errors.sales_channel_id && touched.sales_channel_id && errors.sales_channel_id
                }
              />
              <SelectMolecule testId="sales-order-create-courier-package-select"
                label="Kurir"
                name="courier_package"
                options={[
                  {
                    value: "PAXEL",
                    label: "PAXEL",
                  },
                  {
                    value: "EZ",
                    label: "J&T Express",
                  },
                  {
                    value: "JAGOPACK",
                    label: "LION - JAGOPACK",
                  },
                  {
                    value: "REGPACK",
                    label: "LION - REGPACK",
                  },
                  {
                    value: "BIGPACK",
                    label: "LION - BIGPACK",
                  },
                  {
                    value: "BOSSPACK",
                    label: "LION - BOSSPACK",
                  },
                  {
                    value: "CTC",
                    label: "JNE City Courier",
                  },
                  {
                    value: "JTR",
                    label: "JNE Trucking",
                  },
                  {
                    value: "CTCYES",
                    label: "JNE City Courier YES",
                  },
                ]}
                onChange={(e) => {
                  handleChange(e);

                  if (e.target.value !== "PAXEL") {
                    getDeliveryFee(
                      values.address_id,
                      values.items,
                      e.target.value
                    );
                  }
                  setSelectedCourier(e.target.value);
                  setNeedUpdateDeliveryFee(true);
                }}
              />
            </FormHeader>

            <div className="py-2 overflow-x-auto">
              <div className="w-full">
                <FieldArray name="items">
                  {(arrayHelpers) => (
                    <div className="">
                      <Table.Wrapper asIndex={false} overflow={false}>
                        <Table.Header>
                          <Table.HeaderRow>
                            <Table.Heading>Product</Table.Heading>
                            <Table.Heading>Size</Table.Heading>
                            <Table.Heading>Warna</Table.Heading>
                            {/* <Table.Heading>Catatan</Table.Heading> */}
                            <Table.Heading>Qty</Table.Heading>
                            <Table.Heading>
                              <p className="text-right">Harga Satuan</p>
                            </Table.Heading>
                            <Table.Heading>
                              <p className="text-right">Diskon</p>
                            </Table.Heading>
                            <Table.Heading>
                              <p className="text-right">Subtotal</p>
                            </Table.Heading>
                            <Table.Heading></Table.Heading>
                          </Table.HeaderRow>
                        </Table.Header>
                        <Table.Body>
                          {values.items?.map((orderDetail, key) => (
                            <>
                              <Table.BodyRow key={key}>
                                <Table.Cell className="">
                                  <div className="flex gap-2 items-center">
                                    {orderDetail?.imageUrl && (
                                      <ImageWithPreview
                                        size="lg"
                                        src={`${process.env.REACT_APP_AWS_LINK_URL}${orderDetail?.imageUrl}`}
                                      ></ImageWithPreview>
                                    )}
                                    {/* Lebar dibatasi: nama panjang dipotong "…" alih-alih
                                        melebarkan kolom ini dan memeras Qty/Diskon. */}
                                    <div className="w-full min-w-[14rem] max-w-xs">
                                      <ProductSelectSearchMolecule
                                        hideLabel={true}
                                        name={`items[${key}].product_id`}
                                        selectedProduct={selectedProducts[key]}
                                        onChange={(e) => {
                                          handleChange(e);

                                          // karena produk diganti, harus kalkulasi ongkir ulang
                                          setNeedUpdateDeliveryFee(true);
                                        }}
                                        apiSearch={true}
                                        onBlur={handleBlur}
                                        value={orderDetail?.product_id}
                                        errorMessage={
                                          getIn(
                                            errors,
                                            `items[${key}].product_id`
                                          ) &&
                                          getIn(
                                            touched,
                                            `items[${key}].product_id`
                                          ) &&
                                          getIn(
                                            errors,
                                            `items[${key}].product_id`
                                          )
                                        }
                                        absolute={true}
                                        onProductSelected={(selectedProduct) => {
                                          handleProductSelected(
                                            selectedProduct,
                                            key
                                          );
                                        }}
                                        disabled={true}
                                      />
                                    </div>
                                  </div>
                                </Table.Cell>
                                <Table.Cell className="w-24 [&>*]:min-w-[7rem]">
                                  <SelectMolecule testId="sales-order-create-items-size-variant-id-select"
                                    hideLabel={true}
                                    name={`items[${key}].size_variant_id`}
                                    options={getVariantOptionsWithFallback(
                                      selectedProducts[key],
                                      values.items[`${key}`]?.product_id,
                                      "size",
                                      values.items[`${key}`]?.color_variant_id,
                                      orderDetail
                                    )}
                                    onChange={(e) => {
                                      handleChange(e);
                                    }}
                                    onBlur={handleBlur}
                                    value={orderDetail?.size_variant_id}
                                    errorMessage={
                                      getIn(
                                        errors,
                                        `items[${key}].size_variant_id`
                                      ) &&
                                      getIn(
                                        touched,
                                        `items[${key}].size_variant_id`
                                      ) &&
                                      getIn(
                                        errors,
                                        `items[${key}].size_variant_id`
                                      )
                                    }
                                    absolute={true}
                                    disabled={true}
                                  />
                                </Table.Cell>
                                <Table.Cell className="max-w-xs [&>*]:min-w-[7rem]">
                                  <SelectMolecule testId="sales-order-create-items-color-variant-id-select"
                                    hideLabel={true}
                                    name={`items[${key}].color_variant_id`}
                                    options={getVariantOptionsWithFallback(
                                      selectedProducts[key],
                                      values.items[`${key}`]?.product_id,
                                      "color",
                                      values.items[`${key}`]?.size_variant_id,
                                      orderDetail
                                    )}
                                    onChange={(e) => {
                                      handleChange(e);
                                    }}
                                    onBlur={handleBlur}
                                    value={orderDetail?.color_variant_id}
                                    errorMessage={
                                      getIn(
                                        errors,
                                        `items[${key}].color_variant_id`
                                      ) &&
                                      getIn(
                                        touched,
                                        `items[${key}].color_variant_id`
                                      ) &&
                                      getIn(
                                        errors,
                                        `items[${key}].color_variant_id`
                                      )
                                    }
                                    absolute={true}
                                    disabled={true}
                                  />
                                </Table.Cell>
                                <Table.Cell className="w-24 [&>*]:min-w-[5rem]">
                                  <InputMolecule testId="sales-order-create-items-qty-input"
                                    hideLabel={true}
                                    type="number"
                                    name={`items[${key}].qty`}
                                    onChange={(e) => {
                                      handleChange(e);
                                      setNeedUpdateDeliveryFee(true);
                                      let valueItems = [...values.items];
                                      valueItems[key].qty = e.target.value;
                                      setFieldValue(`discount_transaction`, values.is_endorsement ? (Number(localHelpers.subtotal(valueItems)) + Number(delivery_fee)) : 0);
                                    }}
                                    onBlur={handleBlur}
                                    value={orderDetail?.qty}
                                    errorMessage={
                                      getIn(errors, `items[${key}].qty`) &&
                                      getIn(touched, `items[${key}].qty`) &&
                                      getIn(errors, `items[${key}].qty`)
                                    }
                                    align="right"
                                  />
                                </Table.Cell>
                                <Table.Cell>
                                  <p className="text-right">
                                    <Typography.Currency
                                      number={orderDetail?.unit_price}
                                    />
                                  </p>
                                </Table.Cell>
                                <Table.Cell className="w-32 [&>*]:min-w-[7rem]">
                                  <InputMolecule testId="sales-order-create-items-discount-reseller-input"
                                    hideLabel={true}
                                    type="text"
                                    name={`items[${key}].discount_reseller`}
                                    onChange={(event) => {
                                      setFieldValue(`items[${key}].discount_reseller`, Number(event.target.value));
                                      values.items[key].discount_reseller = Number(event.target.value);
                                      setFieldValue(`discount_transaction`, values.is_endorsement ? (Number(localHelpers.subtotal(values.items)) + Number(delivery_fee)) : 0);
                                    }}
                                    onBlur={handleBlur}
                                    value={orderDetail?.discount_reseller}
                                    errorMessage={
                                      getIn(
                                        errors,
                                        `items[${key}].discount_reseller`
                                      ) &&
                                      getIn(
                                        touched,
                                        `items[${key}].discount_reseller`
                                      ) &&
                                      getIn(
                                        errors,
                                        `items[${key}].discount_reseller`
                                      )
                                    }
                                    align="right"
                                  />
                                </Table.Cell>
                                <Table.Cell>
                                  <p className="text-right">
                                    <Typography.Currency
                                      number={
                                        orderDetail?.qty *
                                        (orderDetail?.unit_price -
                                          orderDetail?.discount_reseller)
                                      }
                                    />
                                  </p>
                                </Table.Cell>
                                <Table.Cell>
                                  <div className="flex items-center gap-2">
                                    <IconButton testId="sales-order-create-trash-button"
                                      icon={TrashIcon}
                                      onClick={(e) => {
                                        // delete values.items[key];

                                        // Remove the item from values.items using filter
                                        const updatedItems = values.items.filter((_, index) => index !== key);

                                        // Update values.items with the filtered array
                                        setFieldValue('items', updatedItems);

                                        setSelectedProducts(
                                          (prevSelectedProducts) =>
                                            prevSelectedProducts.filter((_, index) => index !== key)
                                        );
                                        // Remove from expanded notes if it exists
                                        const newExpanded = { ...expandedNotes };
                                        delete newExpanded[key];
                                        setExpandedNotes(newExpanded);
                                      }}
                                    />
                                    <IconButton testId="sales-order-create-tampilkan-catatan-button"
                                      icon={expandedNotes[key] ? MinusCircleIcon : PlusIcon}
                                      onClick={() => setExpandedNotes(prev => ({
                                        ...prev,
                                        [key]: !prev[key]
                                      }))}
                                      title={expandedNotes[key] ? "Sembunyikan Catatan" : "Tampilkan Catatan"}
                                      className="text-gray-500 hover:text-blue-500"
                                      data-tip={expandedNotes[key] ? "Sembunyikan Catatan" : "Tampilkan Catatan"}
                                      data-for="note-tooltip"
                                    />
                                  </div>
                                </Table.Cell>
                              </Table.BodyRow>
                              {/* Notes row */}
                              {expandedNotes[key] && (
                                <Table.BodyRow className="bg-gray-50">
                                  <Table.Cell colSpan="100%" className="py-2">
                                    <div className="px-4">
                                      <InputMolecule testId="sales-order-create-items-notes-input"
                                        label="Catatan"
                                        name={`items[${key}].notes`}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        value={orderDetail.notes || ''}
                                        placeholder="Tulis catatan untuk item ini..."
                                        errorMessage={
                                          getIn(errors, `items[${key}].notes`) &&
                                          getIn(touched, `items[${key}].notes`) &&
                                          getIn(errors, `items[${key}].notes`)
                                        }
                                      />
                                    </div>
                                  </Table.Cell>
                                </Table.BodyRow>
                              )}
                            </>
                          ))}
                        </Table.Body>
                      </Table.Wrapper>
                    </div>
                  )}
                </FieldArray>
              </div>
            </div>
            <FormSummary>
              <div className="flex-grow w-full md:max-w-lg order-last md:order-first flex flex-col justify-between gap-2">
                <InputMolecule testId="sales-order-create-note-order-input"
                  label="Catatan Pengiriman"
                  type="text"
                  name="note_order"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.note_order}
                  errorMessage={
                    errors.note_order &&
                    touched.note_order &&
                    errors.note_order
                  }
                />
                {values.customer_id && (
                  <div className="w-fit">
                    <BadgeButton testId="sales-order-create-lihat-invoice-outstanding-button" onClick={(e) => setShowInvoice(true)}>
                      Lihat Invoice Outstanding{" ("}
                      {
                        customerInvoices(values.customer_id)?.filter(
                          (invoice) => invoice.status !== 3
                        ).length
                      }
                      {")"}
                    </BadgeButton>
                  </div>
                )}
              </div>
              <div className="h-full grid grid-cols-2 items-center gap-y-1 gap-x-2 text-right w-fit pr-2">
                <div className="text-xs uppercase text-gray-600 text-right font-light">
                  Subtotal:{" "}
                </div>
                <div className="pr-7">
                  <Typography.Currency
                    number={localHelpers.subtotal(values.items)}
                  />
                </div>

                <div className="text-xs uppercase text-gray-600 text-right font-light">
                  Ongkir (
                  {
                    <Typography.Currency
                      number={localHelpers.totalWeight(values.items)}
                      fontSize="xs"
                    />
                  }{" "}
                  gram)
                </div>
                <div className="flex items-center w-full justify-end">
                  <InputMolecule testId="sales-order-create-delivery-fee-input"
                    hideLabel={true}
                    type="number"
                    name={`delivery_fee`}
                    onChange={(e) => {
                      setDeliveryFee(Math.abs(Number(e.target.value)));
                      setNeedUpdateDeliveryFee(false);
                    }}
                    onBlur={handleBlur}
                    value={delivery_fee}
                    errorMessage={
                      getIn(errors, `delivery_fee`) &&
                      getIn(touched, `delivery_fee`) &&
                      getIn(errors, `delivery_fee`)
                    }
                    align="right"
                  />
                  <IconButton testId="sales-order-create-arrow-path-button"
                    icon={ArrowPathIcon}
                    onClick={(e) => {
                      getDeliveryFee(
                        values.address_id,
                        values.items,
                        selectedCourier
                      );
                      // setNeedUpdateDeliveryFee(false);
                    }}
                    disabled={!needUpdateDeliveryFee}
                    color={needUpdateDeliveryFee ? "accent" : "secondary"}
                  />
                </div>
                <div className="text-xs uppercase text-gray-600 text-right font-light">
                  <span className="mr-3 text-sm text-gray-700">Endorsment</span>
                  <input data-testid="sales-order-create-checkbox-input"
                    type="checkbox"
                    checked={values.is_endorsement}
                    onChange={(event) => {
                      const isEndorsement = !values.is_endorsement;
                      setFieldValue('is_endorsement', isEndorsement);
                      setFieldValue('discount_transaction', isEndorsement ? (Number(localHelpers.subtotal(values.items)) + Number(delivery_fee)) : 0);
                    }}
                  />
                </div>
                <div className="flex items-center w-full justify-end">
                </div>
                <div className="text-xs uppercase text-gray-600 text-right font-light">
                  Diskon Order
                </div>
                <div className="flex items-center w-full justify-end">
                  <InputMolecule testId="sales-order-create-discount-transaction-input"
                    hideLabel={true}
                    type="number"
                    name={`discount_transaction`}
                    onChange={(e) => {
                      setFieldValue('discount_transaction', Math.abs(Number(e.target.value)));
                    }}
                    onBlur={handleBlur}
                    value={values.discount_transaction}
                    errorMessage={
                      getIn(errors, `discount_transaction`) &&
                      getIn(touched, `discount_transaction`) &&
                      getIn(errors, `discount_transaction`)
                    }
                    align="right"
                  />
                </div>
                <div className="text-xs uppercase text-gray-600 text-right font-bold">
                  Total:{" "}
                </div>
                <div className="pr-7">
                  <Typography.Currency
                    number={values.is_endorsement ? 0 : (Number(localHelpers.subtotal(values.items)) + Number(delivery_fee)) - Number(values.discount_transaction)}
                  />
                </div>
              </div>
            </FormSummary>

            <ModalMolecule
              show={showInvoice}
              title="Invoice yang belum lunas"
              onClose={() => setShowInvoice(false)}
            >
              {customerInvoices(values.customer_id)?.filter(
                (invoice) => invoice.status !== 3
              ).length > 0 ? (
                <Table.Wrapper>
                  <Table.Header>
                    <Table.HeaderRow>
                      <Table.Heading>Kode</Table.Heading>
                      <Table.Heading align="right">Total Tagihan</Table.Heading>
                      <Table.Heading align="right">Pembayaran</Table.Heading>
                      <Table.Heading align="right">Kredit</Table.Heading>
                      <Table.Heading align="right">Kurang/Lebih</Table.Heading>
                    </Table.HeaderRow>
                  </Table.Header>
                  <Table.Body>
                    {customerInvoices(values.customer_id)?.map(
                      (invoice, key) => {
                        if (invoice.status !== 3) {
                          return (
                            <Table.BodyRow key={key}>
                              <Table.Cell>
                                <BadgeButton testId="sales-order-create-open-invoice-detail-button"
                                  onClick={() =>
                                    window.open(
                                      "/invoice/detail/" + invoice.id,
                                      "_blank",
                                      "noopener,noreferrer"
                                    )
                                  }
                                >
                                  {invoice.inv_code}
                                </BadgeButton>
                              </Table.Cell>
                              <Table.Cell align="right">
                                <Typography.Currency
                                  number={invoice.total_amount}
                                ></Typography.Currency>
                              </Table.Cell>
                              <Table.Cell align="right">
                                <Typography.Currency
                                  number={invoice.total_payment}
                                ></Typography.Currency>
                              </Table.Cell>
                              <Table.Cell align="right">
                                <Typography.Currency
                                  number={invoice.credit_amount}
                                ></Typography.Currency>
                              </Table.Cell>
                              <Table.Cell align="right">
                                <Typography.Currency
                                  color={
                                    Number(invoice.total_amount) >
                                      Number(invoice.total_payment) +
                                      Number(invoice.credit_amount)
                                      ? "red"
                                      : "green"
                                  }
                                  number={
                                    Number(invoice.total_payment) +
                                    Number(invoice.credit_amount) -
                                    Number(invoice.total_amount)
                                  }
                                />
                              </Table.Cell>
                            </Table.BodyRow>
                          );
                        }
                      }
                    )}
                  </Table.Body>
                </Table.Wrapper>
              ) : (
                <p>Tidak ada invoice yang belum lunas.</p>
              )}
            </ModalMolecule>

            <ModalMolecule
              show={duplicateOrder && !confirmedDuplicateOrder}
              title="Duplicate Order"
              onClose={() => {
                setConfirmedDuplicateOrder(false);
                setDuplicateOrder(false);
              }}
            >
              <p>
                Terdapat order untuk <strong>customer yang sama</strong> di{" "}
                <strong>hari yang sama</strong>. Apakah Anda ingin tetap
                melanjutkan?
              </p>
              <div className="fixed bottom-2 left-0 sm:left-auto right-0 sm:right-4 sm:bottom-4 sm:w-auto z-40 flex justify-end border-t border-gray-100 bg-white sm:bg-transparent p-4 sm:p-0 dark:border-gray-700 dark:bg-gray-800 print:hidden sm:absolute sm:rounded-b-lg sm:border-0 ">
                <Button testId="sales-order-create-lanjut-button"
                  onClick={() => {
                    setConfirmedDuplicateOrder(true);
                    return true;
                  }}
                  type="submit"
                >
                  Lanjut
                </Button>
              </div>
            </ModalMolecule>

            <div className="fixed bottom-0 left-0 right-0 sm:absolute sm:left-auto sm:right-4 sm:bottom-4 sm:w-auto z-40 flex justify-end border-t border-gray-100 bg-white sm:bg-transparent p-4 sm:p-0 dark:border-gray-700 dark:bg-gray-800 print:hidden sm:rounded-b-lg sm:border-0">
              <Button testId="sales-order-create-submit-button"
                onClick={() => runValidations(values)}
                type="submit"
                disabled={isLoading}
              >
                {isLoading
                  ? "Loading..."
                  : `Tambah Order${confirmedDuplicateOrder ? " (duplikat)" : ""
                  }`}
              </Button>
            </div>
          </form>
        )}
      </Formik>
    </MainLayout>
  );
}

export default CreateSalesOrderPage;
